// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { location, LocationDO } from '../../requests';

import Edit from './Edit';

type Props = unknown;

interface State {
  update?: any;
  loading: boolean;
  data?: LocationDO[];
  showGrid?: boolean;
}

export default class List extends React.Component <Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM', 'URW']),
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      showGrid: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const locationArray: LocationDO[] = await location < LocationDO[]>('list') || [];
      this.setLocalState({ data: locationArray });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'LE';
    const title: string = rowId ? `Location Edit: ${rowId}` : 'Location New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'LOCATIONS',
        title: 'Locations',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (item: LocationDO): void => {
    window.store.dispatch(removeTab('LE'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: LocationDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.name}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        location('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load().catch(() => { /* pass */ });
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: any = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      address: 'Address',
      latitude: 'Latitude',
      longitude: 'Longitude'
    };
    const columnsToHide: any = [];
    const columnStyles: any = {
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      address: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
        />
      </div>
    );
  }
}
